import { IUser } from '~/models';
import { ISaltclickChatIdentity } from '../types/SaltclickChat';

/**
 * The subset of the user record the chat session needs. Accepts a partial so it
 * can be fed straight from redux (`selectedChildOffice ?? user`), where a
 * freshly logged-in or DSO-stripped record may still be missing office fields.
 */
type TSaltclickChatUser = Partial<
  Pick<IUser, 'id' | 'email_id' | 'user_type' | 'office_name' | 'time_zone' | 'address'>
>;

const trimmed = (value?: string): string | undefined => {
  const next = value?.trim();

  return next || undefined;
};

/**
 * Builds the identity Saltclick is told about, from the office the user is
 * currently acting as — under an office switch that is the child office, which
 * is what support needs to see, and it keeps `gtAdminPanelId` the same value the
 * widget has always been sent.
 *
 * Returns `null` without an id: `gtAdminPanelId` is what makes the session
 * identifiable, so a record without one is not worth a token or a pre-chat
 * write. GOTU-5918.
 */
export const buildSaltclickChatIdentity = (
  user?: TSaltclickChatUser | null,
): ISaltclickChatIdentity | null => {
  const gtAdminPanelId = trimmed(user?.id);
  if (!user || !gtAdminPanelId) return null;

  return {
    gtAdminPanelId,
    email: trimmed(user.email_id),
    // The account's name is the practice name, the same source PostHog identifies
    // as `name`. An office has no contact first/last name to send.
    name: trimmed(user.office_name),
    userType: trimmed(user.user_type),
    homeState: trimmed(user.address?.[0]?.state),
    timeZone: trimmed(user.time_zone),
  };
};

/**
 * Hidden pre-chat fields the Salesforce deployment actually declares.
 *
 * Salesforce silently discards any field a deployment has not declared, so
 * sending more than this buys nothing and ships values it never stores — `email`
 * among them. That is the same data-minimization call auth-service made when it
 * dropped the email and name claims from the JWT, applied to the other channel.
 *
 * Checked against the live config on 2026-08-14: `forms[0].hiddenFormFields`
 * holds `gtAdminPanelId` alone. Widening this list is all that is needed when
 * Salesforce declares more — `ISaltclickChatIdentity` already carries them.
 * `<scrt2Url>/embeddedservice/v1/embedded-service-config?orgId=<orgId>&esConfigName=<deploymentName>`
 */
const DECLARED_PRECHAT_FIELDS: readonly (keyof ISaltclickChatIdentity)[] = ['gtAdminPanelId'];

/**
 * Flattens the declared subset of the identity for
 * `prechatAPI.setHiddenPrechatFields`, which only takes strings. Absent fields
 * are dropped rather than sent empty, so a partial record never overwrites
 * something Salesforce already knows.
 */
export const toSaltclickPrechatFields = (
  identity: ISaltclickChatIdentity,
): Record<string, string> =>
  DECLARED_PRECHAT_FIELDS.reduce<Record<string, string>>((fields, key) => {
    const value = identity[key];
    if (value) fields[key] = value;

    return fields;
  }, {});
